// @flow
import * as React from 'react';

import debounce from 'lodash/debounce';

import { useDispatch } from 'react-redux';

import type { ChangeHandler } from 'reshaped';

import { selectCartOwner } from '@gandi/cart';
import { selectCurrentId } from '@gandi/redux-sharing';

import {
  LIST_API_LIMIT,
  fetchCustomersList,
  selectIsFetchingCustomers,
} from 'src/Cart/Customer/modules/customers';
import type { ListCustomerState } from 'src/Cart/Customer/types';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { useSearchField } from './useSearchField';

type ReturnType = {
  filteredList: Array<ListCustomerState>,
  searchTerm: string,
  handleSearch: ChangeHandler<string>,
  searching: boolean,
};

export function useReselleeSearch(
  sortedCustomers: Array<ListCustomerState>,
  initialTotalCount: number,
): ReturnType {
  const dispatch = useDispatch();
  const currentId = useAppSelector(selectCurrentId);
  const cartOwnerId = useAppSelector(selectCartOwner);
  const sharingId = cartOwnerId || currentId;

  const { filteredList, searchTerm, handleFilter } = useSearchField(
    sortedCustomers,
    ['name', 'email'],
  );

  const isFetchingCustomers = useAppSelector(state =>
    selectIsFetchingCustomers(state, sharingId),
  );

  const debouncedFetch = React.useMemo(
    () =>
      debounce((searchValue: string) => {
        dispatch(fetchCustomersList(sharingId, searchValue));
      }, 500),
    [dispatch, sharingId],
  );

  React.useEffect(() => {
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  const handleSearch = React.useCallback<ChangeHandler<string>>(
    args => {
      handleFilter(args);

      const value = args.value || '';
      if (initialTotalCount > LIST_API_LIMIT || !value) {
        debouncedFetch(value);
      }
    },
    [handleFilter, initialTotalCount, debouncedFetch],
  );

  return {
    filteredList,
    searchTerm,
    handleSearch,
    searching: isFetchingCustomers,
  };
}
